/*
 * LP Missão Olympia 2026: <olympia-rolagem> (barra de rolagem desenhada),
 * <olympia-cobertura> (player dos vídeos) e <olympia-atletas> (carrossel dos atletas).
 */

// Em bloco: várias seções incluem este arquivo e a classe não pode ser redeclarada.
{
  /* Troca só a barra visível; a rolagem segue nativa. `data-rola` e `data-fim` servem ao CSS. */
  class OlympiaRolagem extends HTMLElement {
    connectedCallback() {
      this.alvo = this.querySelector('[data-rolagem-alvo]');
      this.trilho = this.querySelector('.olympia-rolagem__trilho');
      this.polegar = this.querySelector('.olympia-rolagem__polegar');
      if (!this.alvo || !this.trilho || !this.polegar) return;

      this.atualizar = this.atualizar.bind(this);
      this.alvo.addEventListener('scroll', this.atualizar, { passive: true });
      // Captura: o load das imagens não borbulha.
      this.alvo.addEventListener('load', this.atualizar, true);

      this.redimensionamento = new ResizeObserver(this.atualizar);
      this.redimensionamento.observe(this.alvo);
      this.redimensionamento.observe(this.trilho);
      this.mutacao = new MutationObserver(this.atualizar);
      this.mutacao.observe(this.alvo, { childList: true, subtree: true });

      this.trilho.addEventListener('pointerdown', (ev) => this.arrastar(ev));
      this.atualizar();
    }

    disconnectedCallback() {
      this.redimensionamento?.disconnect();
      this.mutacao?.disconnect();
    }

    atualizar() {
      const { scrollTop, scrollHeight, clientHeight } = this.alvo;
      const sobra = scrollHeight - clientHeight;
      const rola = sobra > 1;

      this.toggleAttribute('data-rola', rola);
      this.toggleAttribute('data-fim', !rola || scrollTop >= sobra - 2);
      if (!rola) return;

      const trilho = this.trilho.clientHeight;
      const polegar = this.polegar.offsetHeight;
      this.polegar.style.transform = `translateY(${(trilho - polegar) * (scrollTop / sobra)}px)`;
    }

    /* Arrastar o polegar rola na proporção; clicar no trilho pula uma tela. */
    arrastar(ev) {
      const { scrollHeight, clientHeight } = this.alvo;
      const sobra = scrollHeight - clientHeight;
      if (sobra <= 1) return;

      if (ev.target !== this.polegar) {
        const acima = ev.clientY < this.polegar.getBoundingClientRect().top;
        this.alvo.scrollBy({ top: acima ? -clientHeight : clientHeight, behavior: 'smooth' });
        return;
      }

      ev.preventDefault();
      const inicioY = ev.clientY;
      const inicioTopo = this.alvo.scrollTop;
      const escala = sobra / (this.trilho.clientHeight - this.polegar.offsetHeight || 1);

      this.trilho.setPointerCapture(ev.pointerId);
      const mover = (e) => {
        this.alvo.scrollTop = inicioTopo + (e.clientY - inicioY) * escala;
      };
      const soltar = () => {
        this.trilho.removeEventListener('pointermove', mover);
        this.trilho.removeEventListener('pointerup', soltar);
        this.trilho.removeEventListener('pointercancel', soltar);
      };
      this.trilho.addEventListener('pointermove', mover);
      this.trilho.addEventListener('pointerup', soltar);
      this.trilho.addEventListener('pointercancel', soltar);
    }
  }

  /* O iframe só é criado no clique, para não carregar um embed por vídeo. */
  class OlympiaCobertura extends HTMLElement {
    connectedCallback() {
      this.player = this.querySelector('[data-player]');
      this.quadro = this.querySelector('[data-player-quadro]');
      if (!this.player) return;

      this.addEventListener('click', (ev) => {
        const botao = ev.target.closest('[data-video]');
        if (botao) this.tocar(botao);
      });
      // Desmonta o iframe ao fechar, senão o áudio continua.
      this.player.addEventListener('close', () => this.quadro.replaceChildren());
      this.player.addEventListener('click', (ev) => {
        if (ev.target === this.player) this.player.close();
      });
    }

    tocar(botao) {
      const iframe = document.createElement('iframe');
      iframe.src = botao.dataset.video;
      iframe.title = botao.dataset.titulo || 'Vídeo';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;

      this.quadro.classList.toggle('olympia-cob__player-quadro--vertical', 'vertical' in botao.dataset);
      this.quadro.replaceChildren(iframe);
      this.player.showModal();
    }
  }

  /* No toque, marca com `data-atual` o card do centro (o destaque do hover no desktop). */
  class OlympiaAtletas extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('.olympia-atleta')];
      if (!this.cards.length) return;

      this.agendar = this.agendar.bind(this);
      this.addEventListener('scroll', this.agendar, { passive: true });
      this.redimensionamento = new ResizeObserver(this.agendar);
      this.redimensionamento.observe(this);
      this.prepararAbertura();
      this.marcar();
    }

    disconnectedCallback() {
      this.redimensionamento?.disconnect();
      this.visibilidade?.disconnect();
      clearTimeout(this.atraso);
      cancelAnimationFrame(this.quadro);
      cancelAnimationFrame(this.animacao);
    }

    centroDe(card) {
      const caixa = this.getBoundingClientRect();
      const r = card.getBoundingClientRect();
      return this.scrollLeft + r.left + r.width / 2 - (caixa.left + caixa.width / 2);
    }

    /*
     * Mobile: a lista espera invisível no último card e, ao aparecer na tela, desliza
     * até o segundo. Com movimento reduzido ou só dois atletas, vai direto.
     */
    prepararAbertura() {
      const segundo = this.cards[1];
      if (!segundo || !matchMedia('(max-width: 749px)').matches) return;

      const ultimo = this.cards[this.cards.length - 1];
      if (ultimo === segundo || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.scrollTo({ left: this.centroDe(segundo), behavior: 'instant' });
        return;
      }

      this.scrollTo({ left: this.centroDe(ultimo), behavior: 'instant' });
      this.dataset.abertura = 'espera';

      // Espera o load e mais 2s para não disputar quadros com o carregamento.
      const observar = () => {
        this.atraso = setTimeout(() => {
          if (!this.isConnected) return;
          this.visibilidade = new IntersectionObserver(
            (entradas) => {
              if (!entradas.some((entrada) => entrada.isIntersecting)) return;
              this.visibilidade.disconnect();
              // Mede antes da entrada, cujo translate desloca os cards.
              const destino = this.centroDe(segundo);
              this.dataset.abertura = 'entrando';
              this.deslizar(destino);
            },
            { threshold: 0.4 }
          );
          this.visibilidade.observe(this);
        }, 100);
      };
      if (document.readyState === 'complete') observar();
      else addEventListener('load', observar, { once: true });
    }

    deslizar(destino) {
      const inicio = this.scrollLeft;
      const duracao = 2000;
      let comeco = null;

      // Sem encaixe durante a animação, senão cada passo puxa para um card.
      this.style.scrollSnapType = 'none';
      this.style.scrollBehavior = 'auto';

      const encerrar = () => {
        cancelAnimationFrame(this.animacao);
        this.style.scrollSnapType = '';
        this.style.scrollBehavior = '';
        this.removeEventListener('pointerdown', encerrar);
      };
      // Tocar na lista interrompe a animação.
      this.addEventListener('pointerdown', encerrar);

      const passo = (agora) => {
        comeco ??= agora;
        const t = Math.min((agora - comeco) / duracao, 1);
        // Ease-out: começa rápido, como se já viesse rolando.
        const suave = 1 - (1 - t) ** 1.5;
        this.scrollLeft = inicio + (destino - inicio) * suave;
        if (t < 1) this.animacao = requestAnimationFrame(passo);
        else encerrar();
      };
      this.animacao = requestAnimationFrame(passo);
    }

    agendar() {
      cancelAnimationFrame(this.quadro);
      this.quadro = requestAnimationFrame(() => this.marcar());
    }

    marcar() {
      const caixa = this.getBoundingClientRect();
      const meio = caixa.left + caixa.width / 2;
      let atual = null;
      let menor = Infinity;

      for (const card of this.cards) {
        const r = card.getBoundingClientRect();
        const distancia = Math.abs(r.left + r.width / 2 - meio);
        if (distancia < menor) {
          menor = distancia;
          atual = card;
        }
      }

      for (const card of this.cards) card.toggleAttribute('data-atual', card === atual);
    }
  }

  if (!customElements.get('olympia-rolagem')) customElements.define('olympia-rolagem', OlympiaRolagem);
  if (!customElements.get('olympia-atletas')) customElements.define('olympia-atletas', OlympiaAtletas);
  if (!customElements.get('olympia-cobertura')) customElements.define('olympia-cobertura', OlympiaCobertura);
}
